<script setup lang="ts">
import { computed, inject, ref } from "vue";
import Icon from "@/design/primitives/Icon.vue";
import { UPLOAD_PORT } from "@/shared/contracts/ports";
import { humanSize } from "@/shared/files/human-size";
import { useFileSrc } from "@/shared/files/use-file-src";
import { kindOf, type TurnAttachment, typeLabel } from "../api/turn-attachments";
import { documentKind, OPEN_CHAT_FILE } from "../composables/chat-document";
import { useAgentsI18n } from "../i18n/use-agents-i18n";

/**
 * Um arquivo que o Ben entregou, na conversa.
 *
 * ── O QUE ISTO SUBSTITUI ──────────────────────────────────────────────────
 *
 * Pedido um arquivo no painel, a resposta era colar o conteúdo como texto e
 * oferecer mandá-lo "por WhatsApp ou e-mail" — para quem está olhando a tela, um
 * absurdo. O arquivo existe, a rota dos bytes existe; faltava a conversa dar os
 * dois gestos que importam: ABRIR e BAIXAR.
 *
 * ── UMA IMAGEM SE VÊ; TODO O RESTO É UM CARTÃO ────────────────────────────
 *
 * E um tipo que não se conhece TAMBÉM é um cartão — nunca o sumiço da tela. Um
 * anexo que o painel não sabe nomear ainda tem nome, peso e as duas ações; a
 * ignorância sobre o mime não pode virar um arquivo que a pessoa não recebe. A
 * imagem que não carregar (`@error`) cai para o mesmo cartão pelo mesmo motivo.
 *
 * ── DE ONDE VEM O ENDEREÇO ────────────────────────────────────────────────
 *
 * Da `UploadPort`, que é o único lugar do painel que monta URL de arquivo (I-S4) —
 * a mesma porta do avatar e dos widgets. O `download` é PEDIDO a ela e não colado
 * na URL depois: no driver de objeto a assinatura cobre o `content-disposition`, e
 * um parâmetro acrescentado depois de assinar é um 403 intermitente.
 */
const props = defineProps<{ file: TurnAttachment }>();

const { t } = useAgentsI18n();
const openPreview = inject(OPEN_CHAT_FILE, null);
const uploads = inject(UPLOAD_PORT, null);
const busy = ref(false);
const broken = ref(false);
const failure = ref("");

/** Só imagem resolve o endereço na hora: é ela que precisa dele para existir. Um
 *  documento resolve no clique, e assim uma thread com dez anexos não faz dez
 *  consultas para desenhar dez cartões. */
const wantsThumbnail = computed(() => kindOf(props.file.mime) === "image" && !broken.value);
const src = useFileSrc(() => (wantsThumbnail.value ? props.file.id : null));
const showImage = computed(() => wantsThumbnail.value && !!src.value);

/**
 * ÁUDIO se toca onde está, como imagem se vê — uma nota de voz que exige
 * "abrir noutra aba" para ser ouvida não é uma conversa (dono, 20/08). O
 * endereço resolve na hora pelo mesmo caminho da miniatura; falhando, o cartão
 * de sempre continua embaixo com abrir/baixar.
 */
const wantsAudio = computed(() => props.file.mime.startsWith("audio/"));
const audioSrc = useFileSrc(() => (wantsAudio.value ? props.file.id : null));

/** A file with no name of its own is still a file, and still has to be openable. */
const shownName = computed(() => props.file.name || t("attach.file"));

const caption = computed(() => `${typeLabel(props.file, t)} · ${humanSize(props.file.sizeBytes)}`);

async function reveal(download: boolean): Promise<void> {
	if (
		!download &&
		openPreview &&
		(props.file.mime.startsWith("image/") || documentKind(props.file.name, props.file.mime))
	) {
		openPreview(props.file);
		return;
	}
	failure.value = "";
	if (!uploads) {
		failure.value = t("attach.noPort");
		return;
	}
	busy.value = true;
	try {
		const { url } = await uploads.urlFor(props.file.id, { download });
		// Absoluta: no driver local o servidor responde um caminho relativo, e uma
		// aba nova precisa de um endereço que se resolva sozinho.
		const href = new URL(url, window.location.origin).href;
		if (download) save(href);
		else window.open(href, "_blank", "noopener");
	} catch {
		failure.value = download ? t("attach.downloadFailed") : t("attach.openFailed");
	} finally {
		busy.value = false;
	}
}

/** O `download` do link é o que basta na mesma origem; numa URL assinada de outro
 *  host o navegador o ignora, e quem faz o arquivo baixar é o cabeçalho que o
 *  servidor assinou a pedido. Os dois caminhos terminam no mesmo lugar. */
function save(href: string): void {
	const link = document.createElement("a");
	link.href = href;
	link.download = shownName.value;
	link.rel = "noopener";
	document.body.appendChild(link);
	link.click();
	link.remove();
}
</script>

<template>
	<div class="max-w-full min-w-0 space-y-1 rounded-md border border-border bg-bg-subtle p-1.5">
		<button
			v-if="showImage"
			type="button"
			class="block max-w-full"
			:aria-label="t('attach.openFile', { name: shownName })"
			@click="reveal(false)"
		>
			<img
				:src="src ?? undefined"
				:alt="shownName"
				loading="lazy"
				class="max-h-64 max-w-full rounded object-contain"
				@error="broken = true"
			/>
		</button>

		<audio v-if="wantsAudio && audioSrc" :src="audioSrc" controls class="h-9 w-full max-w-64" />

		<!-- AS PALAVRAS da nota de voz, em itálico sob o tocador — o mesmo desenho
		     da caixa de entrada (`inbox/message/Attachment.vue`), porque é a mesma
		     leitura: ouvir nem sempre é possível, e uma resposta que só existe em
		     som é uma resposta que metade das vezes ninguém consome. -->
		<p v-if="wantsAudio && file.transcript" class="px-0.5 text-xs break-words text-fg-muted italic">
			{{ file.transcript }}
		</p>

		<div class="flex min-w-0 items-center gap-2">
			<Icon v-if="!showImage" name="document" class="size-4 text-fg-subtle" />
			<span class="min-w-0 flex-1 truncate text-xs text-fg" :title="shownName">
				{{ shownName }}
			</span>
			<span class="shrink-0 text-3xs text-fg-subtle">{{ caption }}</span>
		</div>

		<div class="flex items-center gap-2 text-3xs">
			<button
				type="button"
				class="text-accent hover:underline disabled:opacity-50"
				:disabled="busy"
				@click="reveal(false)"
			>
				{{ t("attach.open") }}
			</button>
			<button
				type="button"
				class="text-accent hover:underline disabled:opacity-50"
				:disabled="busy"
				@click="reveal(true)"
			>
				{{ t("attach.download") }}
			</button>
			<span v-if="failure" class="min-w-0 truncate text-danger">{{ failure }}</span>
		</div>
	</div>
</template>
